import { useEffect } from 'react';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { _notNil } from '@/littledash';
import AnimalsTable from '@/components/Animals/AnimalsTable/AnimalsTable';
import Header from '@/components/UI/Header';
import type { Animal, AnimalApiId } from '@/model/Animal.model';
import { FetchProvider, useFetchEntity } from '@/support/Hooks/fetch';
import type { Study } from '@/model/Study.model';
import type { TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { FilterTypes } from '@/constants/FilterTypes';

const createDefaultLivingStatusFilter = (): TableFilter => ({
  category: {
    name: 'Living status',
    value: 'deceased',
    index: 0,
  },
  operation: {
    name: FilterTypes.eq,
    index: 0,
  },
  option: {
    name: 'Alive',
    value: 'null',
  },
});

export const AnimalSelection: FC = () => {
  const { id: studyId } = useParams();
  const { setValue } = useFormContext();
  const { entity: study, entityLoading: studyLoading } = useFetchEntity<Study>({
    entityType: 'study',
    params: { id: studyId },
    includes: 'users,metadata,study_groups',
  });

  useEffect(() => {
    setValue('animals', []);
  }, []);

  const handleSelection = (selectedFlatRows: Array<{ original: Animal }>) => {
    setValue(
      'animals',
      selectedFlatRows.reduce<Array<AnimalApiId>>((acc, row) => {
        const id = row.original.api_id;
        if (_notNil(id)) {
          acc.push(id);
        }
        return acc;
      }, [])
    );
  };

  const animalsLoaded = () => {
    setValue('animalsLoaded', true);
  };

  return (
    <>
      <div className="ph4 pv3">
        <Header mainHeaderText="Choose animals" />
      </div>
      {!studyLoading && _notNil(study) && (
        <div className="ph4" data-test-component="AnimalSelection">
          <FetchProvider entity={study}>
            <AnimalsTable
              enableBulkActions={false}
              onSelectRow={handleSelection}
              onFetchTableData={animalsLoaded}
              defaultFilters={[createDefaultLivingStatusFilter()]}
            />
          </FetchProvider>
        </div>
      )}
    </>
  );
};
